import { useTranslation } from "next-i18next";
import { Dispatch, useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import VinForm, {
  VinFormInput,
  VinFormMethods,
} from "@common/components/forms/VinForm";
import { FieldErrors } from "react-hook-form";
import { useVinDecodeMutation } from "@modules/vehicle/hooks/useVinDecodeQuery";
import { VinDecodeResponse } from "@modules/vehicle/types/VinDecodeResponse";
import {
  FindMyVehicleInformationFormInput,
  VehicleInformationFieldsInput,
} from "../types/BillOfSaleFormFields";
import VehicleInformationForm from "./VehicleInformationForm";
import {
  BillOfSaleFormState,
  BillOfSaleStepAction,
  BillOfSaleStepActions,
} from "../hooks/useBillOfSaleFormStepsReducer";

type Props = {
  billOfSaleReducer: [BillOfSaleFormState, Dispatch<BillOfSaleStepAction>];
  onBack(): void;
  onNext: (
    data: FindMyVehicleInformationFormInput & VehicleInformationFieldsInput
  ) => void;
  onVinChange?: (vin: string) => void;
  onVinDecode?: (vinDecodeData: VinDecodeResponse | undefined) => void;
};

const VehicleDetailsStep = ({
  billOfSaleReducer,
  onBack,
  onNext,
  onVinChange,
  onVinDecode,
}: Props) => {
  const { t } = useTranslation(["common"]);
  const router = useRouter();
  const locale = router.locale || "en";
  const [
    { formInformation, isVinDecodedFromQueryParams, vinDecodedInfo },
    dispatch,
  ] = billOfSaleReducer;
  const useVinDecode = useVinDecodeMutation();
  const { mutate: vinDecode } = useVinDecode;
  const [isStepLoaded, setIsStepLoaded] = useState(false);
  const [formVin, setFormVin] = useState<string>();
  const [formVinErrors, setFormVinErrors] =
    useState<FieldErrors<VinFormInput>>();

  const vinFormRef = useRef<VinFormMethods>(null);

  const handleVinFormSubmit = (data: VinDecodeResponse | undefined) => {
    if (onVinDecode) onVinDecode(data);
  };

  const handleVinDecode = async (form: VinFormInput) => {
    const vehicleInfo = await useVinDecode.mutateAsync({
      vin: form.vin,
      localeCode: router.locale ?? "en",
    });

    return vehicleInfo;
  };

  const handleVinChange = (vin: string) => {
    setFormVin(vin);
    if (onVinChange) onVinChange(vin);
  };

  const handleVinError = (errors: FieldErrors<VinFormInput>) => {
    setFormVinErrors(errors);
  };

  const handleVehicleInformationFormSubmit = async (
    data: VehicleInformationFieldsInput
  ) => {
    await vinFormRef.current?.validateForm();

    if (formVinErrors?.vin) return;

    const vinValue = formInformation.vehicleDetails.vin || formVin;

    if (!vinValue) {
      return;
    }

    onNext({
      ...data,
      vin: formInformation.vehicleDetails.vin || formVin || "",
    });
  };

  const handleVehicleInformationFormSubmitValidate = async () => {
    await vinFormRef.current?.validateForm();
  };

  useEffect(() => {
    setIsStepLoaded(true);
  }, []);

  useEffect(() => {
    const { vin } = formInformation.vehicleDetails;

    if (!vin || isVinDecodedFromQueryParams || isStepLoaded) return;

    vinDecode(
      {
        vin,
        localeCode: locale,
      },
      {
        onSuccess: (data) => {
          dispatch({
            type: BillOfSaleStepActions.SetVinDecodedFromQueryParams,
            payload: true,
          });

          dispatch({
            type: BillOfSaleStepActions.SetVinDecodedInfo,
            payload: data,
          });

          if (onVinDecode) onVinDecode(data);
        },
      }
    );
  }, [
    dispatch,
    formInformation.vehicleDetails,
    isStepLoaded,
    isVinDecodedFromQueryParams,
    locale,
    onVinDecode,
    vinDecode,
    vinDecodedInfo,
  ]);

  return (
    <>
      <div className="mt-6 p-8 w-full border rounded-xl border-gray-200">
        <h4 className="font-semibold kijiji:font-medium autocan:font-medium pb-6">
          {t("common:provide_vin")}
        </h4>
        <VinForm
          vin={
            formInformation?.vehicleDetails?.vin || vinDecodedInfo?.vin || ""
          }
          onSubmit={handleVinFormSubmit}
          onVinDecode={handleVinDecode}
          onVinChange={handleVinChange}
          onError={handleVinError}
          ref={vinFormRef}
        />
      </div>

      <div className="mt-6 p-8 w-full border rounded-xl border-gray-200">
        <VehicleInformationForm
          vehicle={formInformation.vehicleDetails}
          vinDecodedInfo={vinDecodedInfo}
          onSubmit={handleVehicleInformationFormSubmit}
          onSubmitValidate={handleVehicleInformationFormSubmitValidate}
          onBack={onBack}
        />
      </div>
    </>
  );
};

export default VehicleDetailsStep;
